import numeral from 'numeral';
import type React from 'react';
import { useState } from 'react';
import { useAppSelector } from '../flux/configureStore';

// Constellation
import {
  AdornedInput,
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';

// Misc / utils
import { augmentError, formatForLog } from '../utils/errors';
import { logger } from '../index';
import type { PartialFilterType } from '../models/Types';

const NoZestimateModal = ({
  isOpen,
  onClose,
  onSetAppError,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSetAppError: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const setFilter = useAppSelector((state) => state.setFilter);
  const [inputPrice, updateInputPrice] = useState('');
  const [errorMsg, updateErrorMsg] = useState('');
  const userProperty = useAppSelector((state) => state.address);
  const { street, unit, city, state, zip } = userProperty;
  const address = `${street}${unit && unit !== ' ' ? ', Unit ' + unit : ''}, ${city}, ${state} ${zip}`;

  const handleCancel = () => {
    updateErrorMsg('');
    onClose();
  };

  const handlePriceChange = (e: React.FormEvent<HTMLInputElement>) => {
    updateErrorMsg('');

    // @ts-ignore https://github.com/desktop/desktop/issues/297
    const enteredValue = e.target.value;

    updateInputPrice(enteredValue ? numeral(enteredValue).format('0,0') : '');
  };

  const handleSubmit = async () => {
    const price = numeral(inputPrice).value();
    if (price === null) {
      updateErrorMsg('Enter an amount');
      return;
    }

    const partialFilterObj: PartialFilterType = { price };

    try {
      await setFilter(partialFilterObj);
      onSetAppError('');
    } catch (err) {
      augmentError(err, {
        root: 'components/ChangePriceModal#handleSave',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter price change');
    }

    onClose();
  };

  return (
    <Modal
      size={{ base: 'fullScreen', lg: 'md' }}
      onOpenChange={(open) => !open && handleCancel()}
      open={isOpen}
      header={
        <Heading level={4}>
          Rent Zestimate<sup>®</sup> not found
        </Heading>
      }
      body={
        <div>
          <Paragraph css={{ marginTop: '400', marginBottom: '200' }}>
            We&apos;re unable to provide a Rent Zestimate<sup>®</sup> for <strong>{`${address}`}</strong>.
          </Paragraph>
          <Paragraph css={{ marginBottom: '400' }}>
            Enter a custom price and we&apos;ll show you nearby rental listings closest to the price you choose.
          </Paragraph>
          <FormField
            label={<Label>Custom price</Label>}
            required
            error={errorMsg !== ''}
            control={
              <AdornedInput
                input={<AdornedInput.Input onChange={handlePriceChange} value={inputPrice} />}
                startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                css={{ marginTop: '200' }}
                size="lg"
              />
            }
            description={errorMsg !== '' ? <FormHelp>{errorMsg}</FormHelp> : undefined}
          />
          <Box css={{ marginTop: '200', marginBottom: '400' }} />
        </div>
      }
      footer={
        <ButtonGroup aria-label="See listings">
          <TextButton onClick={handleCancel}>Cancel</TextButton>
          <Button tone="brand" emphasis="filled" onClick={handleSubmit}>
            See listings
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default NoZestimateModal;
